import React, { useEffect, useMemo, useState } from 'react';
import type { ContactRole, ProspectContactRecord, ProspectRecord } from '../types/prospecting';
import { formatMdyTime, nowIso } from '../types/prospecting';
import {
  deleteProspectContact,
  getProspect,
  getProspectCallLogs,
  getProspectContacts,
  setProspectBestContact,
  upsertProspect,
  upsertProspectContact
} from '../services/firebase';

type AppUser = { id: string; email: string; name: string; role: 'admin' | 'user' | 'SuperAdmin'; createdAt: string };
const QUALITY_LABELS: Record<1 | 2 | 3 | 4 | 5, string> = {
  1: 'Very Low',
  2: 'Low',
  3: 'Medium',
  4: 'High',
  5: 'Premium'
};

export default function ProspectDetailModal(props: {
  currentUser: AppUser;
  prospectId: string;
  onClose: () => void;
}) {
  const { currentUser, prospectId, onClose } = props;
  const [prospect, setProspect] = useState<ProspectRecord | null>(null);
  const [contacts, setContacts] = useState<ProspectContactRecord[]>([]);
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [customReminderDate, setCustomReminderDate] = useState('');
  const [unavailableRecordingIds, setUnavailableRecordingIds] = useState<Record<string, boolean>>({});

  const [newContact, setNewContact] = useState<{
    name: string;
    role: ContactRole;
    isDecisionMaker: boolean;
    phone: string;
    email: string;
    notes: string;
  }>({ name: '', role: 'receptionist', isDecisionMaker: false, phone: '', email: '', notes: '' });
  const [editingContactId, setEditingContactId] = useState<string | null>(null);

  const bestContactId = prospect?.bestContactId || '';

  const roleOptions: { value: ContactRole; label: string }[] = useMemo(
    () => [
      { value: 'receptionist', label: 'Receptionist' },
      { value: 'manager', label: 'Manager' },
      { value: 'owner', label: 'Owner' },
      { value: 'marketing_manager', label: 'Marketing manager' },
      { value: 'other', label: 'Other' }
    ],
    []
  );

  const refresh = async () => {
    setLoading(true);
    try {
      const p = await getProspect(prospectId);
      setProspect(p);
      setCustomReminderDate(p?.nextReminderAt ? new Date(p.nextReminderAt).toISOString().slice(0, 10) : '');
      const c = await getProspectContacts(currentUser.id, prospectId).catch(() => []);
      setContacts(c);
      const l = await getProspectCallLogs(currentUser.id, prospectId).catch(() => []);
      setLogs(l);
      setUnavailableRecordingIds({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prospectId, currentUser.id]);

  const resetContactForm = () => {
    setNewContact({ name: '', role: 'receptionist', isDecisionMaker: false, phone: '', email: '', notes: '' });
    setEditingContactId(null);
  };

  const saveContact = async () => {
    const existingContact = editingContactId ? contacts.find((c) => c.id === editingContactId) : null;
    const contactId = editingContactId || `${prospectId}_${Date.now()}`;
    await upsertProspectContact(currentUser.id, contactId, {
      id: contactId,
      userId: currentUser.id,
      prospectId,
      name: newContact.name.trim() || undefined,
      role: newContact.role,
      isDecisionMaker: newContact.isDecisionMaker,
      phone: newContact.phone.trim() || undefined,
      email: newContact.email.trim() || undefined,
      notes: newContact.notes.trim() || undefined,
      createdAt: existingContact?.createdAt || nowIso(),
      updatedAt: nowIso()
    } as any);
    resetContactForm();
    await refresh();
  };

  const startEditingContact = (contact: ProspectContactRecord) => {
    setEditingContactId(contact.id);
    setNewContact({
      name: contact.name || '',
      role: contact.role,
      isDecisionMaker: contact.isDecisionMaker,
      phone: contact.phone || '',
      email: contact.email || '',
      notes: contact.notes || ''
    });
  };

  const removeContact = async (contact: ProspectContactRecord) => {
    const confirmed = window.confirm(`Delete contact "${contact.name || contact.role}"?`);
    if (!confirmed) return;
    await deleteProspectContact(currentUser.id, contact.id);
    if (bestContactId === contact.id) {
      await setProspectBestContact(currentUser.id, prospectId, null);
    }
    await refresh();
  };

  const setReminder = async (daysFromNow: number) => {
    const d = new Date();
    d.setDate(d.getDate() + daysFromNow);
    await upsertProspect(currentUser.id, prospectId, {
      userId: currentUser.id,
      nextReminderAt: d.toISOString(),
      updatedAt: nowIso()
    } as any);
    await refresh();
  };

  const setCustomReminder = async () => {
    if (!customReminderDate) {
      alert('Please choose a reminder date.');
      return;
    }
    const chosen = new Date(`${customReminderDate}T12:00:00`);
    if (Number.isNaN(chosen.getTime())) {
      alert('Invalid reminder date.');
      return;
    }
    await upsertProspect(currentUser.id, prospectId, {
      userId: currentUser.id,
      nextReminderAt: chosen.toISOString(),
      updatedAt: nowIso()
    } as any);
    await refresh();
  };

  const setTargetQuality = async (score: 1 | 2 | 3 | 4 | 5) => {
    await upsertProspect(currentUser.id, prospectId, {
      userId: currentUser.id,
      targetQualityScore: score,
      updatedAt: nowIso()
    } as any);
    await refresh();
  };

  const getLogTimestamp = (log: any) => {
    return new Date(log.timestamp || log.createdAt?.toDate?.() || log.createdAt || Date.now());
  };

  const getLogSummary = (log: any): string => {
    const summary = typeof log.summary === 'string' ? log.summary.trim() : '';
    if (summary) return summary;
    const note = typeof log.note === 'string' ? log.note.trim() : '';
    return note;
  };

  const getSupplementalNote = (log: any): string => {
    const summary = typeof log.summary === 'string' ? log.summary.trim() : '';
    const note = typeof log.note === 'string' ? log.note.trim() : '';
    if (!summary || !note || summary === note) return '';
    return note;
  };

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div>
            <h3 style={{ margin: 0 }}>{prospect?.name || prospectId}</h3>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
              {prospect?.categoryLabel || prospect?.categoryGroup || 'Business'}{prospect?.address ? ` • ${prospect.address}` : ''}
            </div>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>
            Close
          </button>
        </div>

        {loading ? (
          <div style={{ padding: 12, color: '#6b7280' }}>Loading…</div>
        ) : (
          <div style={{ padding: 12, display: 'grid', gap: 14 }}>
            <section style={styles.section}>
              <div style={styles.sectionHeader}>
                <strong>Target quality</strong>
                <span style={{ fontSize: 12, color: '#6b7280' }}>
                  Current: {prospect?.targetQualityScore ? `${QUALITY_LABELS[prospect.targetQualityScore]} (${prospect.targetQualityScore}/5)` : 'Not rated'}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                {[1, 2, 3, 4, 5].map((value) => {
                  const score = value as 1 | 2 | 3 | 4 | 5;
                  const active = !!prospect?.targetQualityScore && score <= prospect.targetQualityScore;
                  return (
                  <button
                    key={value}
                    style={{
                      ...styles.starBtn,
                      color: active ? '#f59e0b' : '#d1d5db'
                    }}
                    onClick={() => void setTargetQuality(score)}
                    title={`${value} - ${QUALITY_LABELS[score]}`}
                  >
                    ★
                  </button>
                  );
                })}
                {prospect?.targetQualityScore ? (
                  <span style={{ fontSize: 12, color: '#374151', marginLeft: 6 }}>
                    {QUALITY_LABELS[prospect.targetQualityScore]}
                  </span>
                ) : (
                  <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 6 }}>Not rated</span>
                )}
              </div>
            </section>

            <section style={styles.section}>
              <div style={styles.sectionHeader}>
                <strong>Contacts</strong>
                <span style={{ fontSize: 12, color: '#6b7280' }}>Roles + decision maker</span>
              </div>

              {contacts.length === 0 ? (
                <div style={{ fontSize: 12, color: '#6b7280' }}>No contacts yet.</div>
              ) : (
                <div style={{ display: 'grid', gap: 8 }}>
                  {contacts.map((c) => (
                    <div key={c.id} style={styles.contactRow}>
                      <div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <strong style={{ fontSize: 13 }}>{c.name || c.role}</strong>
                          <span style={{ fontSize: 12, color: '#6b7280' }}>{c.role}</span>
                          {c.isDecisionMaker && <span style={styles.badge}>DM</span>}
                          {c.id === bestContactId && <span style={styles.badgeBlue}>Best</span>}
                        </div>
                        <div style={{ fontSize: 12, color: '#374151', marginTop: 2 }}>
                          {c.phone ? `📞 ${c.phone}` : ''}{c.email ? ` • ✉️ ${c.email}` : ''}
                        </div>
                        {c.notes ? (
                          <div style={{ fontSize: 12, color: '#4b5563', marginTop: 4, whiteSpace: 'pre-wrap' }}>{c.notes}</div>
                        ) : null}
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          style={styles.smallBtn}
                          onClick={() => {
                            if (c.phone) window.open(`tel:${c.phone}`, '_self');
                          }}
                          disabled={!c.phone}
                        >
                          Call
                        </button>
                        <button
                          style={styles.smallBtn}
                          onClick={() => startEditingContact(c)}
                        >
                          Edit
                        </button>
                        <button
                          style={styles.smallBtn}
                          onClick={async () => {
                            await setProspectBestContact(currentUser.id, prospectId, c.id);
                            await refresh();
                          }}
                        >
                          Set best
                        </button>
                        <button
                          style={styles.smallBtnDanger}
                          onClick={() => void removeContact(c)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: 10, borderTop: '1px solid #e5e7eb', paddingTop: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 13 }}>{editingContactId ? 'Edit contact' : 'Add contact'}</strong>
                  {editingContactId ? (
                    <button style={styles.smallBtn} onClick={resetContactForm}>
                      Cancel edit
                    </button>
                  ) : null}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <input
                    style={styles.input}
                    placeholder="Name (optional)"
                    value={newContact.name}
                    onChange={(e) => setNewContact((p) => ({ ...p, name: e.target.value }))}
                  />
                  <select
                    style={styles.input}
                    value={newContact.role}
                    onChange={(e) => setNewContact((p) => ({ ...p, role: e.target.value as ContactRole }))}
                  >
                    {roleOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <input
                    style={styles.input}
                    placeholder="Phone"
                    value={newContact.phone}
                    onChange={(e) => setNewContact((p) => ({ ...p, phone: e.target.value }))}
                  />
                  <input
                    style={styles.input}
                    placeholder="Email"
                    value={newContact.email}
                    onChange={(e) => setNewContact((p) => ({ ...p, email: e.target.value }))}
                  />
                </div>
                <textarea
                  style={{ ...styles.input, minHeight: 72, resize: 'vertical', marginTop: 10 }}
                  placeholder="Notes"
                  value={newContact.notes}
                  onChange={(e) => setNewContact((p) => ({ ...p, notes: e.target.value }))}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 12 }}>
                  <input
                    type="checkbox"
                    checked={newContact.isDecisionMaker}
                    onChange={(e) => setNewContact((p) => ({ ...p, isDecisionMaker: e.target.checked }))}
                  />
                  Decision maker
                </label>
                <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                  <button style={styles.smallBtn} onClick={() => void saveContact()}>
                    {editingContactId ? 'Save changes' : 'Add contact'}
                  </button>
                </div>
              </div>
            </section>

            <section style={styles.section}>
              <div style={styles.sectionHeader}>
                <strong>Reminders</strong>
                <span style={{ fontSize: 12, color: '#6b7280' }}>
                  Next: {prospect?.nextReminderAt ? formatMdyTime(new Date(prospect.nextReminderAt)) : 'None'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button style={styles.smallBtn} onClick={() => void setReminder(1)}>
                  Tomorrow
                </button>
                <button style={styles.smallBtn} onClick={() => void setReminder(3)}>
                  3 days
                </button>
                <button style={styles.smallBtn} onClick={() => void setReminder(7)}>
                  1 week
                </button>
              </div>
              <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="date"
                  style={{ ...styles.input, width: 180, padding: '8px 10px' }}
                  value={customReminderDate}
                  onChange={(e) => setCustomReminderDate(e.target.value)}
                />
                <button style={styles.smallBtn} onClick={() => void setCustomReminder()}>
                  Set custom date
                </button>
              </div>
            </section>

            <section style={styles.section}>
              <div style={styles.sectionHeader}>
                <strong>Call history</strong>
                <span style={{ fontSize: 12, color: '#6b7280' }}>{logs.length} entries</span>
              </div>
              {logs.length === 0 ? (
                <div style={{ fontSize: 12, color: '#6b7280' }}>No call activity yet.</div>
              ) : (
                <div style={{ display: 'grid', gap: 8 }}>
                  {logs.slice(0, 20).map((l) => {
                    const summaryText = getLogSummary(l);
                    const supplementalNote = getSupplementalNote(l);
                    const showRecording = !!l.recordingUrl && !unavailableRecordingIds[l.id];
                    return (
                      <div key={l.id} style={styles.logRow}>
                        <div style={{ fontSize: 12, color: '#374151' }}>
                          <strong>{l.outcome || l.response || 'Call'}</strong> • {formatMdyTime(getLogTimestamp(l))}
                        </div>
                        {summaryText ? <div style={styles.logSummary}>{summaryText}</div> : null}
                        {supplementalNote ? <div style={styles.logSupplemental}>{supplementalNote}</div> : null}
                        {showRecording ? (
                          <div style={{ marginTop: 8 }}>
                            <audio
                              controls
                              preload="none"
                              src={l.recordingUrl}
                              style={styles.audioPlayer}
                              onError={() =>
                                setUnavailableRecordingIds((prev) => ({
                                  ...prev,
                                  [l.id]: true
                                }))
                              }
                            />
                          </div>
                        ) : l.recordingUrl ? (
                          <div style={styles.recordingUnavailable}>Recording is no longer available.</div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2000,
    padding: 12
  },
  modal: {
    width: 'min(900px, 96vw)',
    maxHeight: '92vh',
    overflow: 'auto',
    background: '#fff',
    borderRadius: 14,
    border: '1px solid #e5e7eb',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    borderBottom: '1px solid #e5e7eb',
    position: 'sticky',
    top: 0,
    background: '#fff',
    zIndex: 1
  },
  closeBtn: {
    padding: '8px 10px',
    borderRadius: 10,
    border: '1px solid #e5e7eb',
    background: '#fff',
    cursor: 'pointer',
    fontSize: 12
  },
  section: {
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 12,
    background: '#fafafa'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 12,
    marginBottom: 10,
    flexWrap: 'wrap'
  },
  input: {
    width: '100%',
    padding: '10px 10px',
    borderRadius: 10,
    border: '1px solid #e5e7eb',
    fontSize: 12
  },
  smallBtn: {
    padding: '8px 10px',
    borderRadius: 10,
    border: '1px solid #e5e7eb',
    background: '#fff',
    cursor: 'pointer',
    fontSize: 12
  },
  smallBtnDanger: {
    padding: '8px 10px',
    borderRadius: 10,
    border: '1px solid #fecaca',
    color: '#b91c1c',
    background: '#fff',
    cursor: 'pointer',
    fontSize: 12
  },
  contactRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    alignItems: 'center',
    padding: 10,
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    background: '#fff'
  },
  logRow: {
    fontSize: 12,
    color: '#374151',
    padding: 10,
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    background: '#fff'
  },
  logSummary: {
    marginTop: 6,
    color: '#111827',
    whiteSpace: 'pre-wrap'
  },
  logSupplemental: {
    marginTop: 6,
    color: '#6b7280',
    whiteSpace: 'pre-wrap'
  },
  audioPlayer: {
    width: '100%',
    maxWidth: 420
  },
  recordingUnavailable: {
    marginTop: 8,
    color: '#6b7280'
  },
  badge: {
    fontSize: 11,
    border: '1px solid #a7f3d0',
    color: '#065f46',
    padding: '1px 6px',
    borderRadius: 999
  },
  badgeBlue: {
    fontSize: 11,
    border: '1px solid #93c5fd',
    color: '#1d4ed8',
    padding: '1px 6px',
    borderRadius: 999
  },
  starBtn: {
    border: 'none',
    background: 'transparent',
    padding: '2px 1px',
    fontSize: 22,
    lineHeight: 1,
    cursor: 'pointer'
  }
};

